package com.dotdrives.web

import com.dotdrives.controller.GiverExperienceController
import com.dotdrives.controller.NewReminderController
import com.dotdrives.repository.SupabaseGiverExperienceRepository
import com.dotdrives.repository.SupabaseReminderRepository
import com.dotdrives.web.pages.dashboard.DashboardPortal
import com.dotdrives.controller.AuthController
import com.dotdrives.controller.GiverDetailController
import com.dotdrives.controller.GiversController
import com.dotdrives.controller.NewGiverController
import com.dotdrives.controller.OnboardingController
import com.dotdrives.design.DotButtonVariant
import com.dotdrives.model.OrgMember
import com.dotdrives.repository.SupabaseAuthRepository
import com.dotdrives.repository.SupabaseGiverRepository
import com.dotdrives.repository.SupabaseOrgRepository
import com.dotdrives.repository.SupabaseProfileRepository
// composition root: repositories constructed here only
import com.dotdrives.web.pages.BootstrapErrorPage
import com.dotdrives.web.pages.BrandSplash
import com.dotdrives.web.pages.GalleryPage
import com.dotdrives.web.pages.GivingPortalPage
import com.dotdrives.web.pages.ModulePlaceholderPage
import com.dotdrives.web.pages.OrgPickerPage
import com.dotdrives.web.pages.ResetPasswordPage
import com.dotdrives.web.pages.SignInPage
import com.dotdrives.web.pages.donations.accountDonationsPortal
import com.dotdrives.web.pages.drives.DrivesPortal
import com.dotdrives.web.pages.forms.FormsPortal
import com.dotdrives.web.pages.givers.GiversPage
import com.dotdrives.web.pages.givers.ImportWizard
import com.dotdrives.web.pages.marketing.MarketingLandingPage
import com.dotdrives.web.pages.nextsteps.NextStepsPortal
import com.dotdrives.web.pages.onboarding.OnboardingPage
import com.dotdrives.web.pages.updates.UpdatesPortal
import com.dotdrives.web.pages.updates.ReportsPortal
import com.dotdrives.web.pages.settings.SettingsPortal
import com.dotdrives.web.shell.NavItem
import com.dotdrives.web.shell.PortalShell
import com.dotdrives.web.ui.DotButton
import com.dotdrives.web.ui.useFlowState
import kotlinx.browser.window
import kotlinx.coroutines.MainScope
import kotlinx.coroutines.awaitCancellation
import kotlinx.coroutines.cancel
import kotlinx.coroutines.launch
import react.FC
import react.Key
import react.Props
import react.create
import react.dom.html.ReactHTML.span
import react.useEffect
import react.useState

enum class AppRoute(
    val hash: String,
) {
    Landing("#/"),
    Gallery("#/gallery"),
    Admin("#/admin"),
    SignIn("#/signin"),
    ResetPassword("#/reset-password"),
    Giving("#/giving"),
    PublicGiving("#/give"),
    Invite("#/invite"),
    Onboarding("#/onboarding"),
    Dashboard("#/dashboard"),
    NextSteps("#/next-steps"),
    Drives("#/drives"),
    Givers("#/givers"),
    Donations("#/donations"),
    Forms("#/forms"),
    Updates("#/updates"),
    Reports("#/reports"),
    Settings("#/settings"),
}

// Public pages remain reachable regardless of session or account bootstrap state.
val publicRoutes: Set<AppRoute> = setOf(AppRoute.Landing, AppRoute.PublicGiving)

enum class AuthDestination {
    Admin,
    Invite,
    Splash,
    SignIn,
    BootstrapError,
    Onboarding,
    OrgSelection,
    Giving,
    Public,
    Portal,
}

enum class ResetRouteState {
    Loading,
    Invalid,
    RedirectPortal,
    Form,
}

fun resetRouteState(
    checked: Boolean,
    hasSession: Boolean,
    recoveryPending: Boolean,
): ResetRouteState = when {
    !checked -> ResetRouteState.Loading
    recoveryPending && hasSession -> ResetRouteState.Form
    hasSession -> ResetRouteState.RedirectPortal
    else -> ResetRouteState.Invalid
}

val NavItem.route: AppRoute
    get() = when (this) {
        NavItem.Dashboard -> AppRoute.Dashboard
        NavItem.NextSteps -> AppRoute.NextSteps
        NavItem.Drives -> AppRoute.Drives
        NavItem.Givers -> AppRoute.Givers
        NavItem.Donations -> AppRoute.Donations
        NavItem.Forms -> AppRoute.Forms
        NavItem.Updates -> AppRoute.Updates
        NavItem.Reports -> AppRoute.Reports
        NavItem.Settings -> AppRoute.Settings
    }

val AppRoute.navItem: NavItem?
    get() = when (this) {
        AppRoute.Dashboard -> NavItem.Dashboard
        AppRoute.NextSteps -> NavItem.NextSteps
        AppRoute.Drives -> NavItem.Drives
        AppRoute.Givers -> NavItem.Givers
        AppRoute.Donations -> NavItem.Donations
        AppRoute.Forms -> NavItem.Forms
        AppRoute.Updates -> NavItem.Updates
        AppRoute.Reports -> NavItem.Reports
        AppRoute.Settings -> NavItem.Settings
        AppRoute.Landing,
        AppRoute.Gallery,
        AppRoute.Admin,
        AppRoute.SignIn,
        AppRoute.ResetPassword,
        AppRoute.Giving,
        AppRoute.PublicGiving,
        AppRoute.Invite,
        AppRoute.Onboarding,
        -> null
    }

fun parseAppRoute(hash: String, search: String = ""): AppRoute {
    if (
        search.removePrefix("?").split('&').any { it == "recovery=1" } ||
        hash.removePrefix("#").split('&').any { it == "type=recovery" }
    ) {
        return AppRoute.ResetPassword
    }
    val scoped = parseOrganizationRoute(hash)
    if (scoped.invalid) return AppRoute.Dashboard
    val path = scoped.resourceHash
        .removePrefix("#")
        .substringBefore('?')
        .trimEnd('/')
        .ifBlank { "/" }
    if (path == "/admin" || path.startsWith("/admin/") || ((path == "/" || !hash.startsWith("#/")) && search.removePrefix("?").split('&').any { it == "admin=1" })) return AppRoute.Admin
    if ((path == "/" || !hash.startsWith("#/")) && search.removePrefix("?").split('&').any { it == "signin=1" }) return AppRoute.SignIn
    if (path == "/invite" || path.startsWith("/invite/")) return AppRoute.Invite
    if (path.startsWith("/settings/")) return AppRoute.Settings
    if (path == "/give" || path.startsWith("/give/")) return AppRoute.PublicGiving
    return AppRoute.entries.firstOrNull { it.hash.removePrefix("#") == path }
        ?: AppRoute.Landing
}

fun hasRecoveryRouteHint(hash: String, search: String = ""): Boolean {
    val hashParameters = hash.removePrefix("#").split('&')
    val queryParameters = search.removePrefix("?").split('&')
    return (
        hashParameters.any { it.startsWith("access_token=") } &&
            hashParameters.any { it == "type=recovery" }
        ) || (
        queryParameters.any { it.startsWith("code=") && it.length > "code=".length } &&
            queryParameters.any { it == "type=recovery" }
        )
}

fun recoveryTokenHash(search: String): String? {
    val parameters = search.removePrefix("?")
        .split('&')
        .filter(String::isNotBlank)
        .associate { parameter ->
            parameter.substringBefore('=') to parameter.substringAfter('=', "")
        }
    return parameters["token_hash"]?.takeIf {
        it.isNotBlank() && parameters["type"] == "recovery"
    }
}

// Email/provider callbacks are explicit portal entry. Remember their intent while
// the auth SDK consumes its fragment; an explicit #/ navigation always wins.
fun portalCallbackRoute(hash: String, search: String = ""): AppRoute? {
    if (organizationReturnHash(hash, search) != null || hash.startsWith("#/") || parseAppRoute(hash, search) == AppRoute.ResetPassword) return null
    if (hasGiverCallback(search)) return AppRoute.Giving
    if (search.removePrefix("?").split('&').any {it=="signin=1" || it=="admin=1"}) return AppRoute.SignIn
    if (inviteIdFromLocation(hash, search) != null) return AppRoute.SignIn
    val callback = hash.removePrefix("#").split('&').any { it.startsWith("access_token=") && it.length > "access_token=".length } ||
        search.removePrefix("?").split('&').any { it.startsWith("code=") && it.length > "code=".length }
    return AppRoute.SignIn.takeIf { callback }
}

fun hasGiverCallback(search: String): Boolean =
    search.removePrefix("?").split('&').any { it == "giver=1" }

fun consumeGiverCallbackUrl(pathname: String, search: String, hash: String): String? {
    val parameters = search.removePrefix("?").split('&').filter(String::isNotBlank)
    if (parameters.none { it == "giver=1" }) return null
    val remaining = parameters.filterNot { it == "giver=1" }
    return buildString {
        append(pathname)
        if (remaining.isNotEmpty()) append("?${remaining.joinToString("&")}")
        append(hash)
    }
}

fun routeAfterHashChange(currentRoute: AppRoute, hash: String, search: String = ""): AppRoute {
    val nextRoute = parseAppRoute(hash, search)
    return if (
        currentRoute == AppRoute.ResetPassword &&
        (hash.isBlank() || hash == "#")
    ) {
        AppRoute.ResetPassword
    } else {
        nextRoute
    }
}

/** Staff discovery is for shared sign-in and admin entry, not a customer support route. */
fun staffEntryRequested(hash:String,search:String):Boolean {
    if(inviteIdFromLocation(hash,search)!=null || hasGiverCallback(search) || parseAppRoute(hash,search)==AppRoute.ResetPassword)return false
    return parseAppRoute(hash,search) in setOf(AppRoute.SignIn,AppRoute.Admin) || portalCallbackRoute(hash,search)==AppRoute.SignIn
}

fun authDestination(
    checked: Boolean,
    hasSession: Boolean,
    needsOnboarding: Boolean,
    hasBootstrapError: Boolean = false,
    needsOrgSelection: Boolean = false,
    giverContext: Boolean = false,
    hasOrgMembership: Boolean = false,
    isPublicRoute: Boolean = false,
    publicGiving: Boolean = false,
    inviteRequested: Boolean = false,
    staffCheckPending: Boolean = false,
    staffRole: String? = null,
    staffSignInRequired: Boolean = false,
): AuthDestination = when {
    publicGiving -> AuthDestination.Public
    isPublicRoute -> AuthDestination.Public
    !checked || staffCheckPending -> AuthDestination.Splash
    !hasSession -> AuthDestination.SignIn
    inviteRequested -> AuthDestination.Invite
    hasBootstrapError -> AuthDestination.BootstrapError
    giverContext -> AuthDestination.Giving
    staffRole != null && staffSignInRequired -> AuthDestination.SignIn
    staffRole != null -> AuthDestination.Admin
    needsOrgSelection -> AuthDestination.OrgSelection
    needsOnboarding -> AuthDestination.Onboarding
    else -> AuthDestination.Portal
}

fun routeRedirect(
    destination: AuthDestination,
    currentRoute: AppRoute,
    liveRoute: AppRoute = currentRoute,
): AppRoute? = if (
    liveRoute != currentRoute || currentRoute in publicRoutes || currentRoute == AppRoute.Invite
) null else when (destination) {
    AuthDestination.Admin -> AppRoute.Admin.takeIf { currentRoute != AppRoute.Admin }
    AuthDestination.Invite -> null
    AuthDestination.Splash -> null
    AuthDestination.SignIn -> AppRoute.SignIn.takeIf { currentRoute != AppRoute.SignIn }
    AuthDestination.BootstrapError -> null
    AuthDestination.Onboarding -> AppRoute.Onboarding.takeIf { currentRoute != AppRoute.Onboarding }
    AuthDestination.OrgSelection -> null
    AuthDestination.Giving -> AppRoute.Giving.takeIf { currentRoute != AppRoute.Giving }
    AuthDestination.Public -> null
    AuthDestination.Portal -> when {
        currentRoute == AppRoute.Gallery || currentRoute == AppRoute.ResetPassword -> null
        currentRoute.navItem == null -> AppRoute.Dashboard
        else -> null
    }
}

private data class AuthControllerHolder(
    val scope: kotlinx.coroutines.CoroutineScope,
    val controller: AuthController,
)

private data class OnboardingControllerHolder(
    val scope: kotlinx.coroutines.CoroutineScope,
    val controller: OnboardingController,
)

private data class OnboardingImportHolder(
    val scope: kotlinx.coroutines.CoroutineScope,
    val controller: GiversController,
)

private data class GiversControllerHolder(
    val scope: kotlinx.coroutines.CoroutineScope,
    val directory: GiversController,
    val detail: GiverDetailController,
    val newGiver: NewGiverController,
    val experience: GiverExperienceController,
    val reminder: NewReminderController,
    val orgRepository: SupabaseOrgRepository,
)

fun useAuthController(
    recoveryTokenHash: String? = null,
    recoveryCallbackHint: Boolean = false,
    giverRequested: Boolean = false,
): AuthController {
    val (holder) = useState {
        val scope = MainScope()
        AuthControllerHolder(
            scope = scope,
            controller = AuthController(
                authRepo = SupabaseAuthRepository(
                    recoveryGrantStore = SessionStorageRecoveryGrantStore(),
                ),
                orgRepo = SupabaseOrgRepository(),
                profileRepo = SupabaseProfileRepository(),
                scope = scope,
                shouldCheckStaff = {staffEntryRequested(window.location.hash,window.location.search)},
                requestedOrgId = { parseOrganizationRoute(organizationReturnHash(window.location.hash, window.location.search) ?: window.location.hash).orgId },
                staffEntry = {com.dotdrives.client.AdminClient().entry()},
            ),
        )
    }

    useEffect(holder) {
        holder.controller.start(
            recoveryTokenHash = recoveryTokenHash,
            recoveryCallbackHint = recoveryCallbackHint,
            giverRequested = giverRequested,
        )
        try {
            awaitCancellation()
        } finally {
            holder.scope.cancel()
        }
    }

    return holder.controller
}

private fun useOnboardingController(): OnboardingController {
    val (holder) = useState {
        val scope = MainScope()
        OnboardingControllerHolder(
            scope = scope,
            controller = OnboardingController(
                orgRepo = SupabaseOrgRepository(),
                profileRepo = SupabaseProfileRepository(),
                scope = scope,
            ),
        )
    }

    useEffect(holder) {
        try {
            awaitCancellation()
        } finally {
            holder.scope.cancel()
        }
    }

    return holder.controller
}

private external interface OnboardingRouteProps : Props {
    var authController: AuthController
    var email: String
    var canWrite: Boolean
}

private external interface OnboardingImportProps : Props {
    var orgId: String
    var onClose: () -> Unit
    var onImported: (Int) -> Unit
}

private val OnboardingImport = FC<OnboardingImportProps> { props ->
    val (holder) = useState {
        val scope = MainScope()
        OnboardingImportHolder(
            scope = scope,
            controller = GiversController(SupabaseGiverRepository(), scope, props.orgId),
        )
    }
    useEffect(holder) {
        try {
            awaitCancellation()
        } finally {
            holder.scope.cancel()
        }
    }
    ImportWizard {
        controller = holder.controller
        onClose = props.onClose
        onImported = props.onImported
    }
}

private val OnboardingRoute = FC<OnboardingRouteProps> { props ->
    val controller = useOnboardingController()
    val state = useFlowState(controller.state)
    val (importOpen, setImportOpen) = useState(false)

    useEffect(state.step, state.createdOrgId) {
        if (state.step == 4 && state.createdOrgId != null && !state.finished) {
            setImportOpen(true)
        }
    }

    OnboardingPage {
        this.controller = controller
        this.state = state
        email = props.email
        canWrite = props.canWrite
        onImportGivers = {
            if (state.createdOrgId != null) setImportOpen(true)
        }
        onExploreDashboard = props.authController::refresh
    }
    val orgId = state.createdOrgId
    if (importOpen && orgId != null) {
        OnboardingImport {
            key = orgId.unsafeCast<Key>()
            this.orgId = orgId
            onClose = { setImportOpen(false) }
            onImported = { count ->
                setImportOpen(false)
                controller.completeGiverImport(count)
            }
        }
    }
}

private external interface GiversPortalProps : Props {
    var initialGiverId: String?
    var canFinance: Boolean
    var orgId: String
    var userId: String
    var userInitials: String
    var userName: String
    var orgName: String
    var canWrite: Boolean
    var onSwitchOrg: () -> Unit
    var onNavigate: (NavItem) -> Unit
    var onSignOut: () -> Unit
}

private external interface GiversTopbarActionsProps : Props {
    var onImport: () -> Unit
    var onNewGiver: () -> Unit
}

private val GiversTopbarActions = FC<GiversTopbarActionsProps> { props ->
    DotButton {
        text = "⇧ Import CSV"
        onClick = props.onImport
        className = "givers-topbar-button"
        variant = DotButtonVariant.Secondary
    }
    DotButton {
        text = "+ New giver"
        onClick = props.onNewGiver
        className = "givers-topbar-button"
    }
}

private val GiversPortal = FC<GiversPortalProps> { props ->
    val (holder) = useState {
        val scope = MainScope()
        val repo = SupabaseGiverRepository(includeFinance = props.canFinance)
        val orgRepository = SupabaseOrgRepository()
        GiversControllerHolder(
            scope = scope,
            directory = GiversController(repo, scope, props.orgId),
            detail = GiverDetailController(repo, scope, props.orgId),
            newGiver = NewGiverController(
                repo = repo,
                scope = scope,
                orgId = props.orgId,
                defaultStewardUserId = props.userId,
            ),
            experience = GiverExperienceController(SupabaseGiverExperienceRepository(), scope, props.orgId, props.canFinance, props.canWrite),
            reminder = NewReminderController(SupabaseReminderRepository(), scope, props.orgId, props.userId),
            orgRepository = orgRepository,
        )
    }
    val directoryState = useFlowState(holder.directory.state)
    val detailState = useFlowState(holder.detail.state)
    val experienceState = useFlowState(holder.experience.state)
    val reminderState = useFlowState(holder.reminder.state)
    val newGiverState = useFlowState(holder.newGiver.state)
    val (dialogOpen, setDialogOpen) = useState(false)
    val (importOpen, setImportOpen) = useState(false)
    val (members, setMembers) = useState<List<OrgMember>>(emptyList())

    useEffect(holder) {
        holder.directory.load()
        holder.scope.launch {
            holder.orgRepository.members(props.orgId)
                .onSuccess { setMembers(it) }
        }
        try {
            awaitCancellation()
        } finally {
            holder.scope.cancel()
        }
    }

    useEffect(dialogOpen, importOpen) {
        val keyListener: (dynamic) -> Unit = { event ->
            if (event.key == "Escape") {
                event.preventDefault()
                if (importOpen) {
                    setImportOpen(false)
                } else if (dialogOpen) {
                    setDialogOpen(false)
                }
            }
        }
        window.asDynamic().addEventListener("keydown", keyListener)
        try {
            awaitCancellation()
        } finally {
            window.asDynamic().removeEventListener("keydown", keyListener)
        }
    }

    PortalShell {
        active = NavItem.Givers
        onNavigate = props.onNavigate
        userInitials = props.userInitials
        userName = props.userName
        orgName = props.orgName
        onSwitchOrg = props.onSwitchOrg
        onSignOut = props.onSignOut
        titleSuffix = directoryState.totalCount.toString()
        actions = if (props.canWrite) {
            GiversTopbarActions.create {
                onImport = {
                    setDialogOpen(false)
                    setImportOpen(true)
                }
                onNewGiver = {
                    holder.newGiver.startCreate()
                    setImportOpen(false)
                    setDialogOpen(true)
                }
            }
        } else {
            null
        }
        GiversPage {
            initialGiverId = props.initialGiverId
            orgId = props.orgId
            experienceController = holder.experience
            this.experienceState = experienceState
            reminderController = holder.reminder
            this.reminderState = reminderState
            canFinance = props.canFinance
            giversController = holder.directory
            giversState = directoryState
            detailController = holder.detail
            this.detailState = detailState
            newGiverController = holder.newGiver
            this.newGiverState = newGiverState
            currentUserId = props.userId
            currentUserName = props.userName
            this.members = members
            canWrite = props.canWrite
            this.dialogOpen = dialogOpen
            this.importOpen = importOpen
            onOpenDialog = {
                holder.newGiver.startCreate()
                setImportOpen(false)
                setDialogOpen(true)
            }
            onCloseDialog = { setDialogOpen(false) }
            onOpenImport = {
                setDialogOpen(false)
                setImportOpen(true)
            }
            onCloseImport = { setImportOpen(false) }
        }
    }
}

private val AuthenticatedPortal = FC<Props> {
    val rawLocation = useHashLocation()
    val location = organizationReturnHash(rawLocation, window.location.search) ?: rawLocation
    val organizationRoute = parseOrganizationRoute(location)
    val requestedOrg = organizationRoute.orgId
    val (callbackRoute, setCallbackRoute) = useState {
        portalCallbackRoute(window.location.hash, window.location.search)
    }
    val route = callbackRoute?.takeIf { !location.startsWith("#/") }
        ?: parseAppRoute(location, window.location.search)
    useEffect(location) { window.scrollTo(0.0, 0.0) }
    val entityRoute = parseEntityRoute(location)
    val inviteId = inviteIdFromLocation(location, window.location.search)
    val giverRequested = hasGiverCallback(window.location.search) || route == AppRoute.Giving
    val controller = useAuthController(giverRequested = giverRequested)
    val state = useFlowState(controller.state)
    val wantsStaffEntry=requestedOrg == null && !state.giverContext && !state.recoveryPending && staffEntryRequested(location,window.location.search)
    val staffCheckPending=state.session!=null && state.staffEntryChecked!=wantsStaffEntry
    val orgCheckPending = requestedOrg != null && state.session != null && state.resolvedDeepLinkOrgId != requestedOrg
    useEffect(requestedOrg, state.session?.userId, state.loading, staffCheckPending) {
        if ((orgCheckPending || staffCheckPending) && !state.loading && (state.bootstrapError == null || (requestedOrg != null && state.deepLinkAttemptOrgId != requestedOrg))) controller.refresh()
    }
    val destination = authDestination(
        checked = state.checked,
        hasSession = state.session != null,
        needsOnboarding = state.needsOnboarding,
        hasBootstrapError = state.bootstrapError != null,
        needsOrgSelection = state.needsOrgSelection,
        giverContext = requestedOrg == null && (state.giverContext || route == AppRoute.Giving),
        hasOrgMembership = state.orgs.isNotEmpty(),
        isPublicRoute = route in publicRoutes,
        publicGiving = route == AppRoute.PublicGiving,
        inviteRequested = inviteId != null,
        staffCheckPending = (staffCheckPending || orgCheckPending) && state.bootstrapError==null,
        staffRole = state.staffRole,
        staffSignInRequired = state.staffSignInRequired,
    )

    useEffect(destination, route) {
        // A queued auth effect belongs to its rendered route, not a newer navigation.
        if (inviteId == null && requestedOrg == null && !organizationRoute.invalid) {
            routeRedirect(
                destination,
                route,
                liveRoute = callbackRoute?.takeIf { !window.location.hash.startsWith("#/") }
                    ?: parseAppRoute(window.location.hash, window.location.search),
            )?.let(::navigate)
        }
    }
    useEffect(state.checked, location, state.activeOrgId) {
        val liveLocation = organizationReturnHash(window.location.hash, window.location.search) ?: window.location.hash
        if (liveLocation == location && state.checked && state.session != null && state.bootstrapError == null && !orgCheckPending && !organizationRoute.invalid) {
            val org = requestedOrg ?: state.activeOrgId
            if (org != null && route.navItem != null) {
                val canonical = organizationRouteHash(org, location)
                if (window.location.hash != canonical) {
                    val remaining = window.location.search.removePrefix("?").split('&').filter { it.isNotBlank() && !it.startsWith("return_to=") }
                    window.history.replaceState(null, "", window.location.pathname + (if (remaining.isEmpty()) "" else "?" + remaining.joinToString("&")) + canonical)
                    window.asDynamic().dispatchEvent(js("new Event('hashchange')"))
                }
            }
        }
    }
    useEffect(state.checked, callbackRoute) {
        if (state.checked) {
            if (callbackRoute != null) {
                if (!window.location.hash.startsWith("#/")) navigate(callbackRoute)
                setCallbackRoute(null)
            }
            if(route in setOf(AppRoute.SignIn,AppRoute.Admin)) {
                val remaining=window.location.search.removePrefix("?").split('&').filter{it.isNotBlank() && it!="signin=1" && it!="admin=1"}
                window.history.replaceState(null,"",window.location.pathname+(if(remaining.isEmpty())"" else "?"+remaining.joinToString("&"))+window.location.hash)
            }
            consumeGiverCallbackUrl(
                pathname = window.location.pathname,
                search = window.location.search,
                hash = window.location.hash,
            )?.let { callbackFreeUrl ->
                window.history.replaceState(
                    null,
                    "",
                    callbackFreeUrl,
                )
            }
        }
    }

    if (organizationRoute.invalid || (requestedOrg != null && route.navItem == null)) {
        BootstrapErrorPage {
            message = "This organization link is invalid."
            onRetry = { navigate(AppRoute.SignIn) }
            onSignOut = controller::signOut
        }
        return@FC
    }

    if (route == AppRoute.Invite && inviteId == null) {
        InvalidInvitationPage()
        return@FC
    }

    when (destination) {
        AuthDestination.Admin -> if(route==AppRoute.Admin) com.dotdrives.web.pages.admin.AdminPortal() else BrandSplash()
        AuthDestination.Invite -> InvitePage {
            key = "${state.session?.userId}:$inviteId".unsafeCast<Key>()
            this.inviteId = requireNotNull(inviteId); auth = controller; this.state = state
        }
        AuthDestination.Splash -> BrandSplash()
        AuthDestination.SignIn -> SignInPage {
            this.controller = controller
            this.state = state
        }
        AuthDestination.BootstrapError -> BootstrapErrorPage {
            message = state.bootstrapError ?: "Unable to load your account"
            onRetry = controller::retryBootstrap
            onSignOut = controller::signOut
        }
        AuthDestination.Onboarding -> OnboardingRoute {
            val session = requireNotNull(state.session)
            key = session.userId.unsafeCast<Key>()
            authController = controller
            email = session.email
            canWrite = state.canWrite
        }
        AuthDestination.OrgSelection -> OrgPickerPage {
            orgs = state.orgs
            onSelect = { orgId -> window.location.hash = organizationRouteHash(orgId, if (route.navItem != null) location else AppRoute.Dashboard.hash) }
            onSignOut = controller::signOut
        }
        AuthDestination.Giving -> {
            GivingPortalPage {
                key = state.session?.userId.unsafeCast<Key>()
                email = state.session?.email.orEmpty()
                userId = state.session?.userId.orEmpty()
                hasTeam = state.orgs.isNotEmpty()
                onTeam = { controller.selectAudience(com.dotdrives.model.AuthAudience.Team); navigate(AppRoute.Dashboard) }
                onSignOut = controller::signOut
            }
        }
        AuthDestination.Public -> {
            val onLandingSignIn = {
                controller.showSignIn(preserveAudience = true)
                navigate(AppRoute.SignIn)
            }
            val onLandingStartTrial = {
                controller.showSignUp()
                navigate(AppRoute.SignIn)
            }
            // A concurrent public hash transition remains usable before AppRoot unmounts
            // the authenticated composition. Never redirect it into the staff portal.
            when (route) {
                AppRoute.PublicGiving -> PublicGivingComposition { hash = location }
                AppRoute.Landing -> MarketingLandingPage {
                    onSignIn = onLandingSignIn
                    onStartTrial = onLandingStartTrial
                }
                else -> MarketingLandingPage {
                    onSignIn = onLandingSignIn
                    onStartTrial = onLandingStartTrial
                }
            }
        }
        AuthDestination.Portal -> {
            val active = route.navItem ?: NavItem.Dashboard
            val activeOrg = state.orgs.firstOrNull { it.id == state.activeOrgId }
            val email = state.session?.email.orEmpty()
            val displayName = state.profileDisplayName ?: userDisplayName(email)
            val session = state.session
            com.dotdrives.web.shell.OrganizationSelection.Provider {
                value = com.dotdrives.web.shell.OrganizationSelectionState(state.orgs, state.activeOrgId) { orgId ->
                    if (orgId != state.activeOrgId && state.orgs.any { it.id == orgId }) {
                        // Leave entity links and open workflows in the previous organization.
                        // Every portal below remounts its controllers using user + org identity.
                        window.location.hash = organizationRouteHash(orgId, AppRoute.Dashboard.hash)
                    }
                }
            if (active == NavItem.Dashboard && activeOrg != null && session != null) {
                DashboardPortal {
                    key = "${session.userId}:${activeOrg.id}".unsafeCast<Key>()
                    orgId = activeOrg.id; userId = session.userId; userInitials = userInitials(email)
                    userName = displayName; orgName = activeOrg.name; canFinance = activeOrg.role != "field"; canWrite = state.canWrite
                    onSwitchOrg = { controller.clearOrgSelection(); window.location.hash = AppRoute.Dashboard.hash }; onNavigate = { navigate(it.route, activeOrg?.id) }; onSignOut = controller::signOut
                }
            } else if (active == NavItem.Givers && activeOrg != null && session != null) {
                GiversPortal {
                    key = "${session.userId}:${activeOrg.id}".unsafeCast<Key>()
                    initialGiverId = entityRoute.entityFor(activeOrg.id)
                    canFinance = activeOrg.role != "field"
                    orgId = activeOrg.id
                    userId = session.userId
                    userInitials = userInitials(email)
                    userName = displayName
                    orgName = activeOrg.name
                    canWrite = state.canWrite
                    onSwitchOrg = { controller.clearOrgSelection(); window.location.hash = AppRoute.Dashboard.hash }
                    onNavigate = { navigate(it.route, activeOrg?.id) }
                    onSignOut = controller::signOut
                }
            } else if (active == NavItem.NextSteps && activeOrg != null && session != null) {
                NextStepsPortal {
                    currentUserRole = activeOrg.role
                    key = "${session.userId}:${activeOrg.id}".unsafeCast<Key>()
                    orgId = activeOrg.id
                    userId = session.userId
                    userInitials = userInitials(email)
                    userName = displayName
                    orgName = activeOrg.name
                    canWrite = state.canWrite
                    onSwitchOrg = { controller.clearOrgSelection(); window.location.hash = AppRoute.Dashboard.hash }
                    onNavigate = { navigate(it.route, activeOrg?.id) }
                    onSignOut = controller::signOut
                }
            } else if (active == NavItem.Drives && activeOrg != null && session != null) {
                DrivesPortal {
                    currentUserRole = activeOrg.role
                    key = "${session.userId}:${activeOrg.id}".unsafeCast<Key>()
                    initialDriveId = entityRoute.entityFor(activeOrg.id)
                    initialNewGiverId = entityRoute.newGiverFor(activeOrg.id)
                    openNew = entityRoute.newDriveFor(activeOrg.id)
                    initialTemplateId = entityRoute.newTemplateFor(activeOrg.id)
                    initialPhaseId = entityRoute.newPhaseFor(activeOrg.id)
                    orgId = activeOrg.id
                    userId = session.userId
                    userInitials = userInitials(email)
                    userName = displayName
                    orgName = activeOrg.name
                    canWrite = state.canWrite
                    onSwitchOrg = { controller.clearOrgSelection(); window.location.hash = AppRoute.Dashboard.hash }
                    onNavigate = { navigate(it.route, activeOrg?.id) }
                    onSignOut = controller::signOut
                }
            } else if (active == NavItem.Updates && activeOrg != null && session != null) {
                UpdatesPortal {
                    currentUserRole = activeOrg.role
                    key = "${session.userId}:${activeOrg.id}".unsafeCast<Key>()
                    userInitials = userInitials(email)
                    userName = displayName
                    orgName = activeOrg.name
                    canWrite = state.canWrite
                    orgId = activeOrg.id
                    userId = session.userId
                    onSwitchOrg = { controller.clearOrgSelection(); window.location.hash = AppRoute.Dashboard.hash }
                    onNavigate = { navigate(it.route, activeOrg?.id) }
                    onSignOut = controller::signOut
                }
            } else if (active == NavItem.Reports && activeOrg != null && session != null) {
                ReportsPortal {
                    key = "${session.userId}:${activeOrg.id}".unsafeCast<Key>()
                    currentUserRole = activeOrg.role; orgId = activeOrg.id
                    fiscalStartMonth = activeOrg.fiscalStartMonth; fiscalStartDay = activeOrg.fiscalStartDay
                    userInitials = userInitials(email); userName = displayName; orgName = activeOrg.name
                    onSwitchOrg = { controller.clearOrgSelection(); window.location.hash = AppRoute.Dashboard.hash }; onNavigate = { navigate(it.route, activeOrg?.id) }; onSignOut = controller::signOut
                }
            } else if (active == NavItem.Forms && activeOrg != null && session != null) {
                FormsPortal {
                    key = "${session.userId}:${activeOrg.id}".unsafeCast<Key>()
                    orgId = activeOrg.id
                    userInitials = userInitials(email)
                    userName = displayName
                    orgName = activeOrg.name
                    canWrite = state.canWrite
                    onSwitchOrg = { controller.clearOrgSelection(); window.location.hash = AppRoute.Dashboard.hash }
                    onNavigate = { navigate(it.route, activeOrg?.id) }
                    onSignOut = controller::signOut
                }
            } else if (active == NavItem.Donations && activeOrg != null && session != null) {
                accountDonationsPortal(session.userId, activeOrg.id) {
                    currentUserRole = activeOrg.role
                    userInitials = userInitials(email)
                    userName = displayName
                    orgName = activeOrg.name
                    canWrite = state.canWrite
                    onSwitchOrg = { controller.clearOrgSelection(); window.location.hash = AppRoute.Dashboard.hash }
                    onNavigate = { navigate(it.route, activeOrg?.id) }
                    onSignOut = controller::signOut
                }
            } else if (active == NavItem.Settings && activeOrg != null && session != null) {
                SettingsPortal {
                    onFiscalSaved = controller::updateFiscalPeriod
                    key = "${session.userId}:${activeOrg.id}".unsafeCast<Key>()
                    userId = session.userId
                    orgId = activeOrg.id
                    userInitials = userInitials(email)
                    userName = displayName
                    orgName = activeOrg.name
                    currentUserRole = activeOrg.role
                    readOnly = activeOrg.readOnly
                    supportSession = activeOrg.supportSession != null
                    onSwitchOrg = { controller.clearOrgSelection(); window.location.hash = AppRoute.Dashboard.hash }
                    onNavigate = { navigate(it.route, activeOrg?.id) }
                    onSignOut = controller::signOut
                }
            } else {
                PortalShell {
                    this.active = active
                    onNavigate = { navigate(it.route, activeOrg?.id) }
                    userInitials = userInitials(email)
                    userName = displayName
                    orgName = activeOrg?.name ?: "Dot Drives"
                    onSwitchOrg = { controller.clearOrgSelection(); window.location.hash = AppRoute.Dashboard.hash }
                    onSignOut = controller::signOut
                    ModulePlaceholderPage {
                        module = active
                        onNavigate = { navigate(it.route, activeOrg?.id) }
                    }
                }
            }
            }
        }
    }
}

private val ResetPasswordRoute = FC<Props> {
    val recoveryCallbackHint = hasRecoveryRouteHint(
        hash = window.location.hash,
        search = window.location.search,
    )
    val controller = useAuthController(
        recoveryTokenHash = recoveryTokenHash(window.location.search),
        recoveryCallbackHint = recoveryCallbackHint,
    )
    val state = useFlowState(controller.state)
    val routeState = resetRouteState(
        checked = state.checked,
        hasSession = state.session != null,
        recoveryPending = state.recoveryPending,
    )

    useEffect(routeState, state.passwordUpdated) {
        val liveRoute = routeAfterHashChange(AppRoute.ResetPassword, window.location.hash, window.location.search)
        if (liveRoute == AppRoute.ResetPassword && (state.passwordUpdated || routeState == ResetRouteState.RedirectPortal)) {
            window.location.replace(
                "${window.location.origin}${window.location.pathname}${AppRoute.Dashboard.hash}",
            )
        }
    }
    useEffect(routeState) {
        val liveRoute = routeAfterHashChange(AppRoute.ResetPassword, window.location.hash, window.location.search)
        if (liveRoute == AppRoute.ResetPassword && routeState == ResetRouteState.Form && window.location.hash != AppRoute.ResetPassword.hash) {
            window.history.replaceState(
                null,
                "",
                "${window.location.pathname}?recovery=1${AppRoute.ResetPassword.hash}",
            )
        }
    }

    when (routeState) {
        ResetRouteState.Loading,
        ResetRouteState.RedirectPortal,
        -> BrandSplash()

        ResetRouteState.Invalid,
        ResetRouteState.Form,
        -> ResetPasswordPage {
            this.controller = controller
            this.state = state
            onBackToSignIn = {
                controller.cancelRecovery()
                window.location.replace(
                    "${window.location.origin}${window.location.pathname}${AppRoute.SignIn.hash}",
                )
            }
        }
    }
}

// A separate composition preserves remounting when staff enter/leave customer support.
private val SharedAdminEntry = FC<Props> { AuthenticatedPortal() }

val AppRoot = FC<Props> {
    // Retain recovery-route stickiness when Auth consumes a reset callback hash.
    val hashRoute = useHashRoute()
    val location = useHashLocation()
    val savedPublic = runCatching { window.sessionStorage.getItem("dotdrives.public.route") }.getOrNull()
    val (callbackPublicHash, setCallbackPublicHash) = useState {
        restorePublicGivingHash(location, window.location.search, savedPublic)
    }
    val restored = callbackPublicHash?.takeIf { !location.startsWith("#/") }
        ?: restorePublicGivingHash(location, window.location.search, savedPublic)
    val effective = restored ?: location
    val orgReturn = organizationReturnHash(location, window.location.search)
    val route = if (orgReturn != null) parseAppRoute(orgReturn) else if (restored != null) parseAppRoute(effective, window.location.search) else hashRoute
    useEffect(effective) {
        if (parsePublicGivingRoute(effective) != null) {
            runCatching { window.sessionStorage.setItem("dotdrives.public.route", effective) }
        }
    }
    if (callbackPublicHash != null) {
        PublicGivingAuthCallback {
            hash = callbackPublicHash
            onFinished = { setCallbackPublicHash(null) }
        }
    }
    when (route) {
        AppRoute.PublicGiving -> PublicGivingComposition { hash = effective }
        AppRoute.Gallery -> GalleryPage()
        AppRoute.Admin -> SharedAdminEntry()
        AppRoute.ResetPassword -> ResetPasswordRoute()
        else -> AuthenticatedPortal()
    }
}

private fun useHashRoute(): AppRoute {
    val (route, setRoute) = useState {
        parseAppRoute(window.location.hash, window.location.search)
    }

    useEffect(route) {
        val listener: (dynamic) -> Unit = {
            setRoute(routeAfterHashChange(route, window.location.hash, window.location.search))
        }
        window.asDynamic().addEventListener("hashchange", listener)
        try {
            awaitCancellation()
        } finally {
            window.asDynamic().removeEventListener("hashchange", listener)
        }
    }

    return route
}

private fun navigate(route: AppRoute, orgId: String? = null) {
    val org = orgId ?: parseOrganizationRoute(window.location.hash).orgId
    val target = if (route.navItem != null && org != null) organizationRouteHash(org, route.hash) else route.hash
    if (window.location.hash != target) window.location.hash = target
}

private fun userInitials(email: String): String {
    val parts = emailNameParts(email)
    return when {
        parts.size >= 2 -> parts.take(2).joinToString("") { it.first().uppercase() }
        parts.size == 1 -> parts.first().take(2).uppercase()
        else -> "DD"
    }
}

private fun userDisplayName(email: String): String {
    val parts = emailNameParts(email)
    return parts.joinToString(" ") { part ->
        part.replaceFirstChar { it.uppercase() }
    }.ifBlank { "Dot Drives user" }
}

private fun emailNameParts(email: String): List<String> =
    email.substringBefore('@')
        .split('.', '_', '-')
        .filter { it.isNotBlank() }
